/* @flow */
import React from 'react';
// $FlowIgnore
import { connect } from 'react-redux';

import {
  DynamicEntityDownloadContainer,
  DataFilesManifestsDownloadContainer,
} from 'containers/DownloadContainers';
import { setModal } from 'ducks/modal';
import { DetailsSummaryContainer } from 'containers/DetailsSummaryContainer';
import { HierarchyLinksContainer } from 'containers/HierarchyContainer';

import { Row } from 'components/UIKit/Flex';
import Section from 'components/UIKit/Details/Content/Section';
import { Spinner } from 'components/UIKit/Spinner';
import DangerButton from 'components/UIKit/Button/DangerButton';
import DeleteEntity from 'components/DeleteEntity';

import DetailsPanel from '../DetailsPanel';
import TransactionList from '../TransactionList';
import DynamicEntityDetailsNavigation from './DynamicEntityDetailsNavigation';
import { getSubmittableFileDefinitions } from 'utils/dictionaryHelpers';
import { HelpIcon, TrashIcon } from 'components/icons';
import { Tooltip } from 'components/UIKit/Tooltip';
import HierarchyTooltip from 'components/Tooltips/HierarchyTooltip';
import ExternalLink from 'components/Links/ExternalLink';
import { HARMONIZED_DATA_FILES } from 'constants/constants';
import withRouting from 'utils/withRouting';


function DynamicEntityDetails({
  beingDeleted,
  children,
  defs,
  dispatch,
  links,
  router: { location: { pathname } },
  style,
}: Object): React.Element {
  let entity = (children || {}).item || {};
  let headerId = entity.submitter_id;

  if (links?.links?.length) entity._links = links.links;

  const submittableFileTypes = getSubmittableFileDefinitions({ defs })
    .map(i => i.id);

  let processing = beingDeleted.some(x => x.ids.includes(entity.id));

  // Removing button for Harmonized Data files, as per SUBP-505
  const showManifestsButton = submittableFileTypes.includes(entity.type);
  // || HARMONIZED_DATA_FILES.includes(entity.type);

  const showHierarchy = !HARMONIZED_DATA_FILES.includes(entity.type);
  const isQCReports = pathname.split('/')[3] === 'qc';

  return (
    <DetailsPanel
      style={style}
      headerId={headerId}
      details={children}
      defs={defs}
      NavigationComponent={DynamicEntityDetailsNavigation} >
      <Section id="actions" header="actions">
        <Row style={{ flexWrap: 'wrap' }}>
          <DynamicEntityDownloadContainer customDataTestID={`button-${entity.type}`} >
            {entity.type}
          </DynamicEntityDownloadContainer>

          {showManifestsButton && (
            <DataFilesManifestsDownloadContainer customDataTestID="button-manifest" dataFilesIds={[entity.id]} filename={`manifest_${headerId}.yml`}>
              Manifest
            </DataFilesManifestsDownloadContainer>)}

          <DangerButton
            customDataTestID="button-delete"
            PrefixComponent={
              processing
                ? <Spinner style={{ width: '15px', height: '15px', marginRight: '0.5rem' }} />
                : <TrashIcon style={{ marginRight: '0.2rem' }} />
            }
            disabled={entity.state === 'submitted' || processing}
            onClick={() => dispatch(setModal({
              Component: () => <DeleteEntity entity={entity} />,
            }))}
            style={showManifestsButton
              ? {
                marginTop: '0.5rem',
              }
              : {}}
            >
            {processing ? 'Processing..' : 'Delete'}
          </DangerButton>
        </Row>
      </Section>

      <Section customDataTestID="table-details" id="details">
        <DetailsSummaryContainer entity={entity} entityType={entity.type}/>
      </Section>

      {showHierarchy && (
        <Section
          customDataTestID="table-hierarchy"
          header="hierarchy"
          icon={(
            <Tooltip Component={<HierarchyTooltip />}>
              <ExternalLink
                arias={{ 'aria-label': 'Link to documentation on Hierarchy' }}
                title="Hierarchy"
                href="https://docs.gdc.cancer.gov/Data_Submission_Portal/Users_Guide/Data_Submission_Process/#hierarchy"
                >
                <HelpIcon />
              </ExternalLink>
            </Tooltip>)}
          id="hierarchy" >
          {links.isFetching
            ? <Spinner />
            : <HierarchyLinksContainer entity={entity} />}
        </Section>)}

      {isQCReports || (
        <Section
          customDataTestID="table-transactions"
          count={(entity._transaction_logs || []).length}
          header="transactions"
          id="transactions"
          >
          {(entity._transaction_logs || []).length
            ? <TransactionList>{entity._transaction_logs}</TransactionList>
            : 'No transactions'}
        </Section>
      )}

      <Section
      // this helps the navigation items reach the bottom properly and be clickable
      />
    </DetailsPanel>
  );
}

export default connect(
  state => ({
    beingDeleted: state.details.beingDeleted,
    defs: state.entityDefinitions.defs,
    links: state.links,
  }),
)(withRouting(DynamicEntityDetails));
